import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Award, Users, Target } from "lucide-react";
import heroImage from "@/assets/hero-luxury-car.jpg";

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="About Battle Born Protection Films | XPEL Certified Las Vegas"
        description="Battle Born Protection Films is locally owned, XPEL-certified, and specializes in PPF and ceramic coating for luxury vehicles in Las Vegas, NV. Meet the team."
        canonical="https://battlebornppf.com/about"
      />
      <link rel="preload" as="image" href={heroImage} />
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt="Battle Born Protection Films climate-controlled installation facility in Las Vegas" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 animate-fade-in">
              About Battle Born Protection Films
            </h1>
            <p className="text-xl text-muted-foreground animate-slide-up">
              Las Vegas's premier destination for automotive protection since 2014
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              Our Mission
            </h2>
            <p className="text-lg text-muted-foreground text-center leading-relaxed mb-12">
              At Battle Born Protection Films, we're dedicated to providing discerning vehicle owners with the highest quality automotive protection services available. As an XPEL Certified installer, we combine cutting-edge technology with meticulous craftsmanship to protect your automotive investment while maintaining its pristine appearance.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-card border border-border">
                <CardContent className="p-8">
                  <Target className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-2xl font-heading font-bold mb-3">Our Commitment</h3>
                  <p className="text-muted-foreground">
                    Every vehicle that enters our facility receives the same attention to detail and precision, whether it's a daily driver or an exotic supercar. We believe in doing the job right the first time, every time.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border border-border">
                <CardContent className="p-8">
                  <Shield className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-2xl font-heading font-bold mb-3">Quality First</h3>
                  <p className="text-muted-foreground">
                    We exclusively use premium XPEL products and employ factory-trained installers to ensure your vehicle receives the best protection available in the industry.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              What Sets Us Apart
            </h2>

            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <Award className="w-12 h-12 text-accent" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-3">XPEL Certified Excellence</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    As a certified XPEL installer, we've undergone rigorous training and maintain the highest standards of installation quality. This certification ensures you receive factory-backed warranties and precision computer-cut patterns designed specifically for your vehicle.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <Users className="w-12 h-12 text-accent" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-3">Experienced Team</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Our team brings over a decade of combined experience in automotive protection. We've worked on everything from classic cars to the latest exotic supercars, understanding the unique needs of each vehicle and owner.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <Shield className="w-12 h-12 text-accent" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-3">Climate-Controlled Facility</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Our state-of-the-art, climate-controlled facility ensures optimal installation conditions year-round. This controlled environment is crucial for achieving flawless results and long-lasting protection in the harsh Las Vegas climate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-darker-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              Our Values
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Integrity</h3>
                <p className="text-muted-foreground">
                  Honest recommendations and transparent pricing, always putting your vehicle's needs first
                </p>
              </div>

              <div className="text-center">
                <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Excellence</h3>
                <p className="text-muted-foreground">
                  Uncompromising quality in every installation, backed by industry-leading warranties
                </p>
              </div>

              <div className="text-center">
                <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-accent" />
                </div>
                <h3 className="text-xl font-heading font-bold mb-2">Service</h3>
                <p className="text-muted-foreground">
                  Exceptional customer experience from consultation through final delivery
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Experience the Difference
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Schedule a consultation and see why discerning vehicle owners trust Battle Born
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
